import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import './ChatWidget.css';

// ===== BASE DE CONNAISSANCES SYNA =====
const knowledgeBase = [
  {
    keywords: ['bonjour', 'salut', 'hello', 'bonsoir', 'coucou'],
    reply: "Bonjour ! 👋 Je suis l'assistant SYNA. Je peux vous renseigner sur nos offres, nos 5 piliers, ou vous orienter vers un diagnostic gratuit. Que souhaitez-vous savoir ?"
  },
  {
    keywords: ['pilier', 'piliers', 'offre', 'offres', 'service', 'services'],
    reply: "SYNA s'articule autour de 5 piliers :\n\n01 — Canal client : structurer vos points de contact\n02 — Moteur équipe : organiser votre traitement interne\n03 — Pilotage : installer les bons indicateurs\n04 — Facturation fluide : du devis à l'encaissement\n05 — E-Facturation : conformité 2026\n\nVous souhaitez en savoir plus sur l'un d'eux ?",
    hasContact: false
  },
  {
    keywords: ['canal', 'client', 'contact', 'demande', 'prospect'],
    reply: "Le pilier Canal Client permet de structurer tous vos points de contact — site, téléphone, email, réseaux — pour qu'aucune demande ne se perde. Résultat : 0 perte de demande et un meilleur taux de conversion. Vous souhaitez un diagnostic ?"
  },
  {
    keywords: ['équipe', 'equipe', 'interne', 'organisation', 'tâche', 'tache', 'workflow'],
    reply: "Le pilier Moteur Équipe organise votre chaîne de traitement interne : réception, affectation, traitement et réponse. Fini les tâches en double et les infos mal transmises. Nos clients gagnent en moyenne 35% de productivité."
  },
  {
    keywords: ['pilotage', 'indicateur', 'kpi', 'suivi', 'performance', 'tableau de bord'],
    reply: "Le pilier Pilotage installe les bons outils de suivi pour garder la main sur vos délais, votre qualité de service et vos performances. Vous aurez enfin une vision claire de votre activité en temps réel."
  },
  {
    keywords: ['facturation', 'facture', 'devis', 'paiement', 'encaissement', 'impayé', 'impaye'],
    reply: "Le pilier Facturation Fluide fiabilise votre cycle de facturation de bout en bout. Nos clients sont payés en moyenne 15 jours plus tôt après la mise en place. Fini les relances épuisantes !"
  },
  {
    keywords: ['e-facturation', 'efacturation', 'électronique', 'electronique', '2026', 'conformité', 'conformite'],
    reply: "L'e-facturation devient obligatoire en 2026 pour toutes les entreprises. SYNA vous accompagne dans cette transition pas à pas, de façon claire et sereine. Mieux vaut anticiper dès maintenant !"
  },
  {
    keywords: ['360', 'pack', 'complet', 'tout', 'ensemble'],
    reply: "Le pack SYNA 360° regroupe les 5 piliers ensemble pour une transformation complète — de la première demande client à l'encaissement, en passant par la conformité e-facturation. C'est notre offre la plus complète et la plus avantageuse."
  },
  {
    keywords: ['prix', 'tarif', 'coût', 'cout', 'combien', 'investissement', 'budget'],
    reply: "Nos tarifs sont personnalisés selon la taille et les besoins de votre entreprise. Nous établissons un devis après le diagnostic initial gratuit de 30 minutes. Voulez-vous réserver votre diagnostic ?"
  },
  {
    keywords: ['diagnostic', 'gratuit', 'rendez-vous', 'rdv', 'réserver', 'reserver', 'appointment'],
    reply: "Notre diagnostic gratuit de 30 minutes permet d'identifier vos points de friction et de définir un plan d'action sur mesure. Aucun engagement ! Vous pouvez le réserver directement via notre formulaire de contact.",
    hasContact: true
  },
  {
    keywords: ['durée', 'duree', 'temps', 'mission', 'combien de temps'],
    reply: "Une mission SYNA dure en moyenne 2 à 6 mois selon la complexité de votre activité. Nous commençons toujours par un diagnostic de 2 semaines pour établir un plan d'action sur mesure."
  },
  {
    keywords: ['taille', 'entreprise', 'pme', 'tpe', 'collaborateur', 'employé', 'employe'],
    reply: "SYNA accompagne les entreprises de 5 à 50 collaborateurs. Si vous ressentez le besoin de structurer sans savoir par où commencer, vous êtes au bon endroit !"
  },
  {
    keywords: ['après', 'apres', 'suivi', 'support', 'après la mission'],
    reply: "Après la mission, vous repartez avec des processus documentés, des outils opérationnels et votre équipe formée. SYNA reste disponible en support pendant 3 mois pour assurer la continuité."
  },
  {
    keywords: ['transport', 'logistique', 'événementiel', 'evenementiel', 'b2b', 'secteur'],
    reply: "SYNA intervient principalement dans plusieurs secteurs : Transport & Logistique (flux, traçabilité, plannings), Événementiel (coordination, multi-projets) et Services B2B (commercial, facturation, relation client). Vous êtes dans l'un de ces secteurs ?"
  },
  {
    keywords: ['merci', 'super', 'parfait', 'excellent', 'bien', 'ok'],
    reply: "Avec plaisir ! 😊 N'hésitez pas si vous avez d'autres questions. Et si vous souhaitez aller plus loin, un diagnostic gratuit de 30 minutes est disponible sans engagement.",
    hasContact: true
  }
];

// Message par défaut si aucun mot-clé trouvé
const defaultReply = {
  reply: "Je n'ai pas bien compris votre question. Vous pouvez me demander des infos sur nos 5 piliers, la durée d'une mission, ou réserver un diagnostic gratuit. Comment puis-je vous aider ?",
  hasContact: false
};

// Suggestions rapides
const quickSuggestions = [
  "Les 5 piliers SYNA",
  "Diagnostic gratuit",
  "E-facturation 2026",
  "Durée d'une mission"
];

const getResponse = (input) => {
  const lowerInput = input.toLowerCase();
  for (const item of knowledgeBase) {
    if (item.keywords.some(keyword => lowerInput.includes(keyword))) {
      return { reply: item.reply, hasContact: item.hasContact || false };
    }
  }
  return defaultReply;
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Bonjour ! 👋 Je suis l'assistant SYNA. Je peux vous renseigner sur nos offres, nos 5 piliers ou vous orienter vers un diagnostic gratuit.\n\nQue souhaitez-vous savoir ?",
      sender: 'bot',
      timestamp: new Date(),
      hasContact: false
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showBadge, setShowBadge] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleOpen = () => {
    setIsOpen(true);
    setShowBadge(false);
  };

  const sendMessage = (text) => {
    const userMessage = {
      id: Date.now(),
      text,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      const { reply, hasContact } = getResponse(text);
      setIsTyping(false);
      const botMessage = {
        id: Date.now() + 1,
        text: reply,
        sender: 'bot',
        timestamp: new Date(),
        hasContact
      };
      setMessages(prev => [...prev, botMessage]);
    }, 900);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    sendMessage(inputValue);
  };

  const handleSuggestion = (suggestion) => {
    sendMessage(suggestion);
  };

  return (
    <>
      {!isOpen && (
        <button
          className="chat-trigger"
          onClick={handleOpen}
          aria-label="Ouvrir le chat"
        >
          <MessageCircle size={24} />
          {showBadge && <span className="chat-badge">1</span>}
        </button>
      )}

      {isOpen && (
        <div className="chat-widget">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-content">
              <div className="chat-avatar">
                <MessageCircle size={20} />
              </div>
              <div className="chat-header-text">
                <h3>SYNA Assistant</h3>
                <span className="chat-status">● En ligne</span>
              </div>
            </div>
            <button
              className="chat-close"
              onClick={() => setIsOpen(false)}
              aria-label="Fermer le chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chat-message ${message.sender === 'user' ? 'user' : 'bot'}`}
              >
                <div className="message-bubble" style={{ whiteSpace: 'pre-line' }}>
                  {message.text}
                </div>
                {message.hasContact && (
                  <Link
                    to="/contact"
                    className="chat-contact-btn"
                    onClick={() => setIsOpen(false)}
                  >
                    Réserver mon diagnostic <ArrowRight size={14} />
                  </Link>
                )}
                <span className="message-time">
                  {message.timestamp.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            ))}

            {isTyping && (
              <div className="chat-message bot">
                <div className="message-bubble typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions rapides */}
          {messages.length <= 2 && (
            <div className="chat-suggestions">
              {quickSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="chat-suggestion-btn"
                  onClick={() => handleSuggestion(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form className="chat-input-form" onSubmit={handleSendMessage}>
            <input
              type="text"
              className="chat-input"
              placeholder="Votre message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <button
              type="submit"
              className="chat-send"
              disabled={!inputValue.trim()}
              aria-label="Envoyer"
            >
              <Send size={20} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;